/**
 * ViewListPage - Main page for view management
 *
 * Displays all views in a DataTable with sorting and pagination. Sort + page
 * state is persisted to URL search params so deep links remain stable.
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, RefreshCw, Download, AlertCircle, Database } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import type { ColumnDef, SortingState, PaginationState } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { DataTable, DataTableSortHeader } from '@/components/ui/data-table';
import { ViewStatusBadge } from '@/components/views/ViewStatusBadge';
import { ViewTypeBadge } from '@/components/views/ViewTypeBadge';
import { ViewActionsCell } from '@/components/views/ViewActionsCell';
import {
  useViews,
  useRefreshView,
  useMaterializeView,
  useDematerializeView,
  useDeleteView,
} from '@/hooks/useViews';
import { useToast } from '@/hooks/useToast';
import { downloadCsv, type CsvExportColumn } from '@/lib/utils/csv-export';
import type { ViewListItem, ViewStatus, ViewType } from '@/types/views';

/**
 * ViewListPage Component
 */
export const ViewListPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const searchTerm = searchParams.get('search') || '';
  const statusFilter = searchParams.get('status') || 'all';
  const typeFilter = searchParams.get('type') || 'all';
  const sortColumn = searchParams.get('sort') || '';
  const sortDirection = searchParams.get('dir') || '';
  const pageParam = searchParams.get('page') || '';
  const [searchInput, setSearchInput] = useState(searchTerm);
  const [refreshingViews, setRefreshingViews] = useState<Set<string>>(new Set());

  /**
   * Debounce search input to URL params (300ms delay).
   */
  useEffect(() => {
    const timer = setTimeout(() => {
      const currentUrlSearch = searchParams.get('search') || '';
      if (searchInput === currentUrlSearch) return;

      const newParams = new URLSearchParams(searchParams);
      if (searchInput) {
        newParams.set('search', searchInput);
      } else {
        newParams.delete('search');
      }
      setSearchParams(newParams, { replace: true });
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch views
  const { data, isLoading, error, refetch } = useViews({
    status: statusFilter !== 'all' ? (statusFilter as ViewStatus) : undefined,
    view_type: typeFilter !== 'all' ? (typeFilter as ViewType) : undefined,
  });

  const refreshView = useRefreshView();
  const materializeView = useMaterializeView();
  const dematerializeView = useDematerializeView();
  const deleteView = useDeleteView();

  const handleQuery = useCallback(
    (id: string, name: string) => {
      navigate(`/query?view=${id}`);
      toast({
        title: 'Opening Query',
        description: `Loading view "${name}" in query editor`,
      });
    },
    [navigate, toast]
  );

  const handleRefresh = useCallback(
    async (id: string, name: string) => {
      setRefreshingViews((prev) => new Set(prev).add(id));

      try {
        await refreshView.mutateAsync({ view_id: id, force: false });
        toast({
          title: 'Refresh Started',
          description: `Refreshing view "${name}"... This may take a few moments.`,
        });

        setTimeout(() => {
          setRefreshingViews((prev) => {
            const newSet = new Set(prev);
            newSet.delete(id);
            return newSet;
          });
          refetch();
        }, 30000);
      } catch (refreshError: any) {
        setRefreshingViews((prev) => {
          const newSet = new Set(prev);
          newSet.delete(id);
          return newSet;
        });
        console.error('Failed to refresh view:', refreshError);
        toast({
          title: 'Refresh Failed',
          description:
            refreshError.response?.data?.detail || 'Failed to trigger view refresh',
          variant: 'destructive',
        });
      }
    },
    [refreshView, toast, refetch]
  );

  const handleMaterialize = useCallback(
    async (id: string, name: string, currentType: ViewType) => {
      const action = currentType === 'virtual' ? 'materialize' : 'dematerialize';
      const actionLabel = currentType === 'virtual' ? 'Materializing' : 'Dematerializing';

      const confirmed = window.confirm(
        currentType === 'virtual'
          ? `Materialize view "${name}"?\n\nThis will pre-compute and store the view results as Parquet files for faster queries.`
          : `Convert view "${name}" to virtual?\n\nThis will delete the stored materialized data.`
      );
      if (!confirmed) return;

      try {
        if (currentType === 'virtual') {
          await materializeView.mutateAsync({ view_id: id });
        } else {
          await dematerializeView.mutateAsync(id);
        }
        toast({
          title: `${actionLabel} Complete`,
          description: `View "${name}" has been ${action}d successfully`,
        });
      } catch (matError: any) {
        console.error(`Failed to ${action} view:`, matError);
        toast({
          title: `${actionLabel} Failed`,
          description: matError.response?.data?.detail || `Failed to ${action} view`,
          variant: 'destructive',
        });
      }
    },
    [materializeView, dematerializeView, toast]
  );

  const handleDelete = useCallback(
    async (id: string, name: string) => {
      try {
        await deleteView.mutateAsync(id);
        toast({
          title: 'View Deleted',
          description: `View "${name}" has been deleted successfully`,
        });
      } catch (deleteError: any) {
        console.error('Failed to delete view:', deleteError);
        toast({
          title: 'Delete Failed',
          description: deleteError.response?.data?.detail || 'Failed to delete view',
          variant: 'destructive',
        });
      }
    },
    [deleteView, toast]
  );

  const handleToggleAutoRefresh = useCallback(
    (_id: string, _currentValue: boolean) => {
      // TODO: Implement auto-refresh toggle API call
      toast({
        title: 'Auto-Refresh Toggle',
        description: 'Auto-refresh toggle will be implemented in a future update',
      });
    },
    [toast]
  );

  const handleRefreshList = useCallback(() => {
    refetch();
    toast({
      title: 'Refreshed',
      description: 'View list has been refreshed',
    });
  }, [refetch, toast]);

  /**
   * Filter row data based on search term
   */
  const filteredData = useMemo<ViewListItem[]>(() => {
    if (!data?.items) return [];
    if (!searchTerm) return data.items;
    const lowerSearch = searchTerm.toLowerCase();
    return data.items.filter(
      (view) =>
        view.name.toLowerCase().includes(lowerSearch) ||
        view.description?.toLowerCase().includes(lowerSearch)
    );
  }, [data, searchTerm]);

  const handleExport = useCallback(() => {
    if (!filteredData.length) {
      toast({ title: 'Nothing to export', description: 'No views match the current filters.' });
      return;
    }
    const csvColumns: CsvExportColumn<ViewListItem>[] = [
      { header: 'Name', accessor: (v) => v.name },
      { header: 'Description', accessor: (v) => v.description ?? '' },
      { header: 'Type', accessor: (v) => v.view_type ?? '' },
      { header: 'Status', accessor: (v) => v.status ?? '' },
      { header: 'Auto-Refresh', accessor: (v) => (v.is_auto_refresh ? 'yes' : 'no') },
      { header: 'File Count', accessor: (v) => (v as any).file_count ?? 0 },
      { header: 'Row Count', accessor: (v) => (v as any).row_count ?? 0 },
      { header: 'Last Refresh', accessor: (v) => v.last_refresh_at ?? '' },
    ];
    downloadCsv(filteredData, csvColumns, `views_${format(new Date(), 'yyyy-MM-dd')}`);
    toast({ title: 'Export Complete', description: 'View data exported to CSV' });
  }, [filteredData, toast]);

  /**
   * URL ↔ DataTable state sync — initial values
   */
  const initialSorting = useMemo<SortingState>(
    () =>
      sortColumn && (sortDirection === 'asc' || sortDirection === 'desc')
        ? [{ id: sortColumn, desc: sortDirection === 'desc' }]
        : [],
    [sortColumn, sortDirection]
  );

  const initialPagination = useMemo<PaginationState>(() => {
    const page = parseInt(pageParam || '0', 10);
    return {
      pageIndex: Number.isFinite(page) && page > 0 ? page : 0,
      pageSize: 20,
    };
  }, [pageParam]);

  const handleSortingChange = useCallback(
    (sorting: SortingState) => {
      const newParams = new URLSearchParams(searchParams);
      const first = sorting[0];
      if (first) {
        newParams.set('sort', first.id);
        newParams.set('dir', first.desc ? 'desc' : 'asc');
      } else {
        newParams.delete('sort');
        newParams.delete('dir');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePaginationChange = useCallback(
    (pagination: PaginationState) => {
      const urlPage = parseInt(searchParams.get('page') || '0', 10);
      if (pagination.pageIndex === urlPage) return;
      const newParams = new URLSearchParams(searchParams);
      if (pagination.pageIndex > 0) {
        newParams.set('page', String(pagination.pageIndex));
      } else {
        newParams.delete('page');
      }
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const columns = useMemo<ColumnDef<ViewListItem>[]>(
    () => [
      {
        accessorKey: 'name',
        header: ({ column }) => <DataTableSortHeader column={column}>Name</DataTableSortHeader>,
        cell: ({ row }) => (
          <span className="font-medium cursor-pointer">{row.original.name}</span>
        ),
      },
      {
        accessorKey: 'description',
        header: 'Description',
        cell: ({ row }) => (
          <span className="text-muted-foreground">
            {row.original.description || 'No description'}
          </span>
        ),
      },
      {
        accessorKey: 'view_type',
        header: ({ column }) => <DataTableSortHeader column={column}>Type</DataTableSortHeader>,
        cell: ({ row }) =>
          row.original.view_type ? <ViewTypeBadge type={row.original.view_type} /> : null,
      },
      {
        accessorKey: 'status',
        header: ({ column }) => <DataTableSortHeader column={column}>Status</DataTableSortHeader>,
        cell: ({ row }) =>
          row.original.status ? <ViewStatusBadge status={row.original.status} /> : null,
      },
      {
        accessorKey: 'is_auto_refresh',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Auto-Refresh</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const view = row.original;
          return (
            <div onClick={(e) => e.stopPropagation()}>
              <Switch
                checked={view.is_auto_refresh}
                onCheckedChange={() => handleToggleAutoRefresh(view.id, view.is_auto_refresh)}
                disabled={view.status === 'failed'}
                aria-label={`Toggle auto-refresh for ${view.name}`}
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'file_count',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>File Count</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = (row.original as any).file_count;
          if (value === undefined || value === null) return <span className="text-muted-foreground">-</span>;
          return value.toLocaleString();
        },
        sortingFn: (a, b) =>
          ((a.original as any).file_count ?? 0) - ((b.original as any).file_count ?? 0),
      },
      {
        accessorKey: 'row_count',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Row Count</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = (row.original as any).row_count;
          if (!value) return <span className="text-muted-foreground">-</span>;
          let formatted: string;
          if (value >= 1000000) {
            formatted = `${(value / 1000000).toFixed(1)}M`;
          } else if (value >= 1000) {
            formatted = `${(value / 1000).toFixed(1)}K`;
          } else {
            formatted = value.toString();
          }
          return <span title={value.toLocaleString()}>{formatted}</span>;
        },
        sortingFn: (a, b) =>
          ((a.original as any).row_count ?? 0) - ((b.original as any).row_count ?? 0),
      },
      {
        accessorKey: 'last_refresh_at',
        header: ({ column }) => (
          <DataTableSortHeader column={column}>Last Refresh</DataTableSortHeader>
        ),
        cell: ({ row }) => {
          const value = row.original.last_refresh_at;
          if (!value) return <span className="text-muted-foreground">Never</span>;
          try {
            const date = new Date(value);
            const relative = formatDistanceToNow(date, { addSuffix: true });
            const absolute = format(date, 'MMM d, yyyy HH:mm');
            return <span title={absolute}>{relative}</span>;
          } catch {
            return value;
          }
        },
      },
      {
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => (
          <div onClick={(e) => e.stopPropagation()}>
            <ViewActionsCell
              data={row.original}
              onQuery={handleQuery}
              onRefresh={handleRefresh}
              onMaterialize={handleMaterialize}
              onDelete={handleDelete}
              refreshingViews={refreshingViews}
            />
          </div>
        ),
      },
    ],
    [handleQuery, handleRefresh, handleMaterialize, handleDelete, handleToggleAutoRefresh, refreshingViews]
  );

  const showEmptyState =
    !isLoading &&
    filteredData.length === 0 &&
    !searchTerm &&
    statusFilter === 'all' &&
    typeFilter === 'all';

  const renderEmptyState = () => (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <Database className="h-16 w-16 text-muted-foreground mb-4" />
      <h3 className="text-xl font-semibold mb-2">No Views Created Yet</h3>
      <p className="text-muted-foreground mb-6 max-w-md">
        Create your first view to aggregate and transform data from multiple files with flexible
        rule-based matching.
      </p>
      <Button onClick={() => navigate('/views/new')}>
        <Plus className="h-4 w-4 mr-2" />
        Create View
      </Button>
    </div>
  );

  return (
    <div className="container mx-auto py-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="text-3xl font-bold">Views</h1>
          <p className="text-muted-foreground">
            Manage data views with flexible file selection and SQL transformations
          </p>
        </div>
        <Button onClick={() => navigate('/views/new')}>
          <Plus className="h-4 w-4 mr-2" />
          Create View
        </Button>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>
            Failed to load views: {error instanceof Error ? error.message : 'Unknown error'}
          </AlertDescription>
        </Alert>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-4 flex-wrap">
        <Input
          placeholder="Search views..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="max-w-sm"
        />
        <Select
          value={statusFilter}
          onValueChange={(value) => {
            const newParams = new URLSearchParams(searchParams);
            if (value && value !== 'all') {
              newParams.set('status', value);
            } else {
              newParams.delete('status');
            }
            setSearchParams(newParams, { replace: true });
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="building">Building</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="paused">Paused</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={typeFilter}
          onValueChange={(value) => {
            const newParams = new URLSearchParams(searchParams);
            if (value && value !== 'all') {
              newParams.set('type', value);
            } else {
              newParams.delete('type');
            }
            setSearchParams(newParams, { replace: true });
          }}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="virtual">Virtual</SelectItem>
            <SelectItem value="materialized">Materialized</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex gap-2 ml-auto">
          <Button variant="outline" onClick={handleRefreshList} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={handleExport} disabled={isLoading}>
            <Download className="h-4 w-4 mr-2" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* DataTable or Empty State */}
      {showEmptyState ? (
        renderEmptyState()
      ) : isLoading ? (
        <div className="flex h-64 items-center justify-center text-muted-foreground">
          Loading…
        </div>
      ) : (
        <DataTable
          data={filteredData}
          columns={columns}
          onRowClick={(v) => navigate(`/views/${v.id}`)}
          initialSorting={initialSorting}
          initialPagination={initialPagination}
          onSortingChange={handleSortingChange}
          onPaginationChange={handlePaginationChange}
          getRowId={(v) => v.id}
          emptyState={
            searchTerm
              ? `No views match "${searchTerm}".`
              : 'No views match the current filters.'
          }
        />
      )}

      {/* Stats */}
      {data && filteredData.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {filteredData.length} of {data.items.length} views
          </span>
          <span>Total: {data.total || data.items.length} views</span>
        </div>
      )}
    </div>
  );
};
